export interface HelpSection {
  id: string;
  title: string;
  items: { question: string; answer: string }[];
}

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    items: [
      {
        question: 'How do I sign up?',
        answer:
          'From the landing page, click "Register" and create an account with your email and password, or use "Continue with Google" to sign in instantly using your Google account.',
      },
      {
        question: 'What shows up on my Home dashboard?',
        answer:
          'Your Home page (accessible via the house icon in the left nav) summarizes your upcoming week\'s events, recent notes, and gives quick launch cards into the AI chat and code editor.',
      },
    ],
  },
  {
    id: 'calendar',
    title: 'Calendar',
    items: [
      {
        question: 'How does AI scheduling work?',
        answer:
          'Type a request in the AI Chat panel on the Calendar page, like "block 2 hours every weekday evening for DSA practice." The AI reads your sleep window and upcoming contests automatically, and will never schedule over a contest or your sleep hours unless you explicitly ask it to.',
      },
      {
        question: 'How do I use voice input?',
        answer:
          'Click the microphone icon next to the chat input on the Calendar page and speak your request naturally. The transcript appears in the input field in real time — click send when you\'re ready, or click the mic again to stop.',
      },
      {
        question: 'Which AI provider is used for scheduling?',
        answer:
          'This is controlled entirely from Settings -> AI Preferences, not from the Calendar page itself. Choose between Ashna AI (managed) or your own Custom AI Agent (Gemini) there — the Calendar chat panel shows which one is currently active as a read-only badge.',
      },
      {
        question: 'Why does an event show a "shifted to avoid..." explanation?',
        answer:
          'Click any AI-scheduled event (marked with a sparkle icon) to see its full reasoning in the sidebar panel — the AI explains any tradeoffs it made, like shortening a sleep block to avoid an overlapping contest.',
      },
      {
        question: 'How do I sync with Google Calendar?',
        answer:
          'Go to Settings -> Account and click "Link Google Calendar." Once linked, events created here automatically push to your real Google Calendar, and your Google Calendar events appear directly on the app\'s own calendar grid too (marked with a green outline and an external-link icon) — no separate preview panel needed to see them.',
      },
      {
        question: 'What happens if a Google Calendar event and an app event are the same thing?',
        answer:
          'Once an app-created event is pushed to Google, it\'s matched by its Google event ID and shown only once, as the editable local version — you won\'t see a duplicate. Events that exist only in Google and were never created here stay read-only on the grid; edit or delete those directly in Google Calendar.',
      },
      {
        question: 'How do I push everything to Google Calendar at once?',
        answer:
          'Click "Push All to Google Calendar" at the top of the Calendar page (or in Settings -> Account). It pushes every event that isn\'t already synced and shows exactly how many succeeded, with the specific reason for any that failed.',
      },
      {
        question: 'What happens when I try to create an event that overlaps another?',
        answer:
          'You get three real choices: Cancel, Allow Overlap (create it anyway), or Adjust Schedule — which automatically finds the next free slot later that same day and asks you to confirm before creating it there instead. If no free slot exists that day, you\'ll see a clear message rather than a silent failure.',
      },
      {
        question: 'Does the app automatically schedule my sleep?',
        answer:
          'Yes — once you save a sleep window in Settings, the next 10 nights are scheduled automatically. If a contest overlaps your sleep window on a given night, that night\'s sleep block shifts to start right after the contest ends instead of being skipped. If you later change your sleep window and already have upcoming auto-scheduled nights, you\'ll be asked whether to replace them before anything changes.',
      },
      {
        question: 'Can I paste in an email or notice and turn it into calendar events?',
        answer:
          'Yes — click "Extract from Text" in the AI Chat panel and paste the text. The AI reads it and returns a structured, editable table (day, event, time, attendance, dress code, notes) for you to review, correct if needed, and confirm before anything is added to your calendar. Nothing is created automatically without that confirmation step.',
      },
      {
        question: 'How do I export my agenda as a PDF?',
        answer:
          'Click "Export Agenda (PDF)" at the top of the Calendar page, choose a range (This Week, This Month, or Next 7 Days), and a PDF downloads with a day-by-day agenda followed by a full reference table of the same events.',
      },
    ],
  },
  {
    id: 'notes',
    title: 'Notes',
    items: [
      {
        question: 'How do I write a note about a specific event?',
        answer:
          'On the Notes page, click "For event," search for and select the event, then write your note. The event\'s title stays visible above the editor so you always know what you\'re writing about.',
      },
      {
        question: 'Does the AI automatically create notes for me?',
        answer:
          'No — notes are always written intentionally by you. AI-scheduled events include their own reasoning (visible when you click the event), but that reasoning is never auto-saved as a note on your behalf.',
      },
      {
        question: 'How does "highlight to ask AI" work?',
        answer:
          'Select any text or code inside a note, and a floating Ashna AI box appears. Choose "Explain," "Review for Errors," "Optimise," or type your own custom question about the highlighted selection.',
      },
    ],
  },
  {
    id: 'code',
    title: 'Code Execution',
    items: [
      {
        question: 'What languages are supported?',
        answer:
          'Python, C++, Java, JavaScript, TypeScript, Go, Rust, and C. Use the language dropdown at the top of the Code page. Execution runs through a fallback chain — if the primary service is unavailable or over its quota, your code automatically retries against a second real execution service before falling back to an AI-simulated result as a last resort (always clearly labeled as simulated, never presented as a real run).',
      },
      {
        question: 'How do I give my program input?',
        answer:
          'Use the "Program Input (stdin)" field below the code editor — anything typed there is piped to your program\'s standard input when you click Run Code.',
      },
      {
        question: 'Can Ashna AI help me understand my code?',
        answer:
          'Click "Ask Ashna" above the editor to get an explanation of your current code, or use the highlight-to-ask feature inside Notes for more targeted questions about a specific snippet.',
      },
      {
        question: 'The output says "AI-simulated" — what does that mean?',
        answer:
          'It means every real code execution service was temporarily unavailable, so an AI reasoned through what your code would likely output instead of actually running it. Treat simulated output as a helpful guess, not a guarantee — if it matters, try running it again once real execution is available.',
      },
    ],
  },
  {
    id: 'contests',
    title: 'Contests',
    items: [
      {
        question: 'Where do contests come from?',
        answer:
          'Codeforces, LeetCode, and CodeChef are automatically scraped every 30 minutes. Filter by platform using the tabs at the top of the Contests page. CodeChef entries lasting 4 hours or longer (long-format/practice-style listings rather than genuine timed contests) are excluded automatically.',
      },
      {
        question: 'Can I schedule practice around a specific contest?',
        answer:
          'Click "Schedule around this" on any contest card to jump into the AI chat with that contest pre-filled as context for your request.',
      },
      {
        question: 'Can I save links to my competitive programming profiles?',
        answer:
          'Yes — the "My CP Profiles" section at the top of the Contests page lets you save links to your Codeforces, LeetCode, or other profiles. Click any saved link to open it directly, or the X next to it to remove it.',
      },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    items: [
      {
        question: 'Where do I change my sleep window?',
        answer:
          'Settings -> Scheduling Preferences. This window is used both by the AI to avoid scheduling anything during your sleep hours, and to automatically generate the next 10 nights of sleep blocks on your calendar. It defaults to 11 PM-6 AM IST if never set.',
      },
      {
        question: 'What happens when I change my sleep window after sleep events already exist?',
        answer:
          'You\'ll be asked to confirm before anything changes — a dialog shows how many upcoming auto-scheduled sleep blocks exist and lets you choose to replace them with a fresh schedule based on your new window, or keep the existing ones as-is.',
      },
      {
        question: 'How do I add my own Gemini API key for the Custom AI Agent?',
        answer:
          'Settings -> AI Preferences -> Custom AI Agent Configuration. Your key is encrypted before storage and never shown again once saved — the field is write-only.',
      },
      {
        question: 'How do I manage active login sessions?',
        answer:
          'Settings -> Security shows every device currently logged into your account. Click "Revoke" next to any session to log it out remotely.',
      },
      {
        question: 'How do I send feedback or report a bug?',
        answer:
          'Scroll to the Feedback section at the bottom of Settings. Choose a category (bug, feature request, general, or praise), optionally rate your experience, write your message, and send — it\'s recorded directly, no email required.',
      },
    ],
  },
];